import { useCallback, useEffect, useRef } from 'react';

/**
 * The props a host uses to drive TextAreaChat from its own chrome. Declared
 * here (not inline in TextAreaChatProps) so the seam and its documentation
 * live with the hook that implements it.
 */
export interface HostHandleProps {
  /** Publishes submit + its enabled/loading state to a host CTA. */
  onRegisterSubmit?: (
    fn: () => void,
    enabled: boolean,
    loading: boolean,
    stopFn?: () => void,
  ) => void;
  /** Publishes the attach path (same one paste/drop/Add-Image use) so a host
   *  can render its own upload affordance without duplicating upload logic. */
  onRegisterAttach?: (addFiles: (files: FileList) => void) => void;
  /** Publishes a prompt-text setter, for host-rendered starter prompts. */
  onRegisterSetText?: (setText: (text: string) => void) => void;
  /** Suppress the built-in attach row when the host renders its own. */
  hideAttachRow?: boolean;
}

/**
 * Publishes TextAreaChat's imperative handles to a host that renders its own
 * chrome — the sidebar Generate button, and the centred prompt card's upload
 * well / "Try" row.
 *
 * Two of these callbacks (`addFiles`, `setText`) close over state that changes
 * every render, so each is handed out as a STABLE wrapper reading a ref.
 * Registering the raw functions would re-fire the effects on every render and,
 * if a host stored one in state, loop.
 */
export function useHostHandles({
  onRegisterSubmit,
  onRegisterAttach,
  onRegisterSetText,
  handleSubmit,
  submitEnabled,
  isLoading,
  stopGenerating,
  addItems,
  setText,
}: {
  onRegisterSubmit?: (
    fn: () => void,
    enabled: boolean,
    loading: boolean,
    stopFn?: () => void,
  ) => void;
  onRegisterAttach?: (addFiles: (files: FileList) => void) => void;
  onRegisterSetText?: (setText: (text: string) => void) => void;
  handleSubmit: () => void;
  submitEnabled: boolean;
  isLoading: boolean;
  stopGenerating?: () => void;
  addItems: (files: FileList) => Promise<void> | void;
  setText: (text: string) => void;
}) {
  // Submit — registered directly; handleSubmit is already memoised upstream.
  useEffect(() => {
    if (!onRegisterSubmit) return;
    onRegisterSubmit(handleSubmit, submitEnabled, isLoading, stopGenerating);
  }, [
    onRegisterSubmit,
    handleSubmit,
    submitEnabled,
    isLoading,
    stopGenerating,
  ]);

  const addItemsRef = useRef(addItems);
  addItemsRef.current = addItems;
  const attachFiles = useCallback((files: FileList) => {
    void addItemsRef.current(files);
  }, []);
  useEffect(() => {
    onRegisterAttach?.(attachFiles);
  }, [onRegisterAttach, attachFiles]);

  const setTextRef = useRef(setText);
  setTextRef.current = setText;
  const setTextStable = useCallback((text: string) => {
    setTextRef.current(text);
  }, []);
  useEffect(() => {
    onRegisterSetText?.(setTextStable);
  }, [onRegisterSetText, setTextStable]);
}
